import { Controller } from "@hotwired/stimulus"
import {
  kindDescription, minPercent, percentHelp, clampPercent, finalPrice, discountError,
  discountSummary, discountSentence, crossesMidnight, windowInvalid, toISO
} from "../modules/discount_schedule.mjs"
import { formatMoney } from "../modules/product_wizard.mjs"

const PRICE_INPUTS = "input[name='product[price]'], [data-money-input-target='display']"

// Tarjeta Descuento del wizard: `kind` decide qué secciones viajan; reglas y textos en
// discount_schedule.mjs, límites desde Discount en los values, estado publicado en data-discount-*.
// Doc: _docs/features/products/partner_product_form.md
export default class extends Controller {
  static targets = [
    "active", "body", "summary", "kindRadio", "kindDesc", "percent", "percentStepper", "percentHelp",
    "windowsSection", "windowsList", "windowRow", "windowTemplate", "addWindow", "timeInput", "destroyFlag", "overnightHint",
    "daysSection", "day", "allDays", "dateSection", "startDate", "endDate", "calendar",
    "final", "finalPrice", "finalBefore", "finalBadge", "finalText"
  ]
  static values = { minPercent: Object, maxWindows: Number, maxDays: Number }

  connect() {
    this.windowSequence = 0
    // El precio vive fuera de la tarjeta: se recalcula con cada tecla en él
    this.form = this.element.closest("form")
    this.onFormInput = event => { if (event.target?.matches?.(PRICE_INPUTS)) this.refresh() }
    this.form?.addEventListener("input", this.onFormInput)
    this.#paintActive()
    this.switchKind()
  }

  disconnect() {
    this.form?.removeEventListener("input", this.onFormInput)
  }

  get kind() {
    return this.kindRadioTargets.find(radio => radio.checked)?.value || "permanent"
  }

  // La cabecera completa es un botón: alterna el switch
  toggleHeader() {
    this.activeTarget.click()
  }

  toggleActive() {
    this.#paintActive()
    if (this.activeTarget.checked) this.#raisePercent()
    this.refresh()
  }

  switchKind() {
    const kind = this.kind
    this.#setSection(this.daysSectionTarget, kind !== "all_day")
    this.#setSection(this.windowsSectionTarget, kind === "flash")
    this.#setSection(this.dateSectionTarget, kind === "all_day")
    this.kindDescTarget.textContent = kindDescription(kind, { mins: this.minPercentValue, maxDays: this.maxDaysValue })
    this.percentHelpTarget.textContent = percentHelp(kind, this.minPercentValue)
    this.percentStepperTarget.dataset.stepperMinValue = minPercent(kind, this.minPercentValue)
    this.#raisePercent()
    if (kind === "flash" && this.liveWindowRows().length === 0) this.addWindow()
    if (kind === "all_day" && !this.startDateTarget.value) this.#calendar()?.setRange(toISO(new Date()))
    this.refresh()
  }

  addWindow() {
    if (this.liveWindowRows().length >= this.maxWindowsValue) return
    // Rails solo acepta índices numéricos en esta colección de atributos anidados
    const index = `${Date.now()}${this.windowSequence++}`
    this.windowsListTarget.insertAdjacentHTML("beforeend", this.windowTemplateTarget.innerHTML.replace(/NEW_WINDOW/g, index))
    this.refresh()
  }

  // No se quita el último horario; un horario guardado se marca para destruir y se oculta
  removeWindow(event) {
    if (this.liveWindowRows().length <= 1) return
    const row = event.currentTarget.closest("[data-discount-form-target='windowRow']")
    if (row.querySelector("input[name$='[id]']")) {
      row.querySelector("[data-discount-form-target='destroyFlag']").value = "1"
      row.querySelectorAll("[data-discount-form-target='timeInput']").forEach(input => { input.disabled = true })
      row.hidden = true
    } else {
      row.remove()
    }
    this.refresh()
  }

  toggleAllDays() {
    const checked = !this.dayTargets.every(day => day.checked)
    this.dayTargets.forEach(day => { day.checked = checked })
    this.refresh()
    this.dayTargets[0]?.dispatchEvent(new Event("change", { bubbles: true }))
  }

  liveWindowRows() {
    return this.windowRowTargets.filter(row => !row.hidden)
  }

  // Borrador local: el estado leído sin el precio (vive fuera de la tarjeta)
  get draft() {
    const { price, ...rest } = this.read()
    return rest
  }

  // Deja la tarjeta como estaba y repinta como cualquier cambio de tipo
  restoreDraft(d) {
    if (!d) return { skipped: 0 }
    this.activeTarget.checked = Boolean(d.active)
    this.#paintActive()
    this.kindRadioTargets.forEach(radio => { radio.checked = radio.value === (d.kind || "permanent") })
    this.percentTarget.value = clampPercent(d.percent, this.kind, this.minPercentValue)
    this.#stepper()?.sync()
    this.dayTargets.forEach((day, index) => { day.checked = Boolean(d.days?.[index]) })
    this.liveWindowRows().filter(row => !row.querySelector("input[name$='[id]']")).forEach(row => row.remove())
    ;(d.windows || []).forEach(window => {
      this.addWindow()
      const inputs = this.liveWindowRows().at(-1)?.querySelectorAll("[data-discount-form-target='timeInput']")
      if (inputs) {
        inputs[0].value = window.start || ""
        inputs[1].value = window.end || ""
      }
    })
    this.startDateTarget.value = d.startDate || ""
    this.endDateTarget.value = d.endDate || ""
    this.switchKind()
    if (d.kind === "all_day" && d.startDate) this.#calendar()?.setRange(d.startDate, d.endDate || d.startDate)
    return { skipped: 0 }
  }

  // Lee el estado del DOM, pinta resumen, horarios y Precio final, y publica data-discount-*
  refresh() {
    const d = this.read()
    const error = discountError(d)
    this.summaryTarget.textContent = discountSummary(d)
    this.allDaysTarget.textContent = d.days.every(Boolean) ? "Quitar todos" : "Todos los días"
    this.addWindowTarget.hidden = this.liveWindowRows().length >= this.maxWindowsValue
    this.#paintWindows()
    this.#paintFinal(d, error)
    const dataset = this.element.dataset
    dataset.discountActive = d.active
    dataset.discountError = error
    dataset.discountPercent = d.percent
    dataset.discountFinal = d.active && !error ? finalPrice(d.price, d.percent) : ""
    // Quitar o agregar un horario no emite input/change: el wizard se entera por este evento
    this.dispatch("change", { detail: { error } })
  }

  read() {
    return {
      active: this.activeTarget.checked,
      kind: this.kind,
      percent: Number(this.percentTarget.value) || 0,
      days: this.dayTargets.map(day => day.checked),
      windows: this.liveWindowRows().map(row => this.#window(row)),
      startDate: this.startDateTarget.value,
      endDate: this.endDateTarget.value,
      price: this.element.closest("form")?.querySelector("input[name='product[price]']")?.value || ""
    }
  }

  #window(row) {
    const inputs = row.querySelectorAll("[data-discount-form-target='timeInput']")
    return { start: inputs[0]?.value || "", end: inputs[1]?.value || "" }
  }

  #paintActive() {
    const on = this.activeTarget.checked
    this.bodyTarget.hidden = !on
    this.bodyTarget.disabled = !on
  }

  #setSection(section, visible) {
    section.hidden = !visible
    section.disabled = !visible
  }

  // El porcentaje sube al piso del tipo cuando hace falta (nunca baja solo)
  #raisePercent() {
    const value = clampPercent(this.percentTarget.value, this.kind, this.minPercentValue)
    if (String(value) !== this.percentTarget.value) {
      this.percentTarget.value = value
      this.#stepper()?.sync()
    }
  }

  // Borde ámbar si el fin es igual al inicio; pista 🌙 si termina al día siguiente
  #paintWindows() {
    const rows = this.liveWindowRows()
    rows.forEach(row => {
      const window = this.#window(row)
      row.classList.toggle("is-invalid", windowInvalid(window))
      const hint = row.querySelector("[data-discount-form-target='overnightHint']")
      if (hint) hint.hidden = !crossesMidnight(window)
      const remove = row.querySelector("[data-action*='removeWindow']")
      if (remove) remove.disabled = rows.length <= 1
    })
  }

  #paintFinal(d, error) {
    const price = Number(d.price) || 0
    this.finalPriceTarget.textContent = formatMoney(finalPrice(price, d.percent))
    this.finalBeforeTarget.textContent = formatMoney(price)
    this.finalBadgeTarget.textContent = `−${d.percent}%`
    this.finalTextTarget.textContent = error || discountSentence(d)
    this.finalTarget.classList.toggle("is-error", Boolean(error))
  }

  #stepper() {
    return this.application.getControllerForElementAndIdentifier(this.percentStepperTarget, "stepper")
  }

  #calendar() {
    return this.hasCalendarTarget ? this.application.getControllerForElementAndIdentifier(this.calendarTarget, "range-calendar") : null
  }
}
